import { useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react'
import { ChevronLeft, Crosshair, Heart, ImageOff, Share } from 'lucide-react'
import { eventPreview } from '../lib/preview'
import type { EventSnapshot } from '../types'

type Focus = { x: number; y: number }

// The cards' crop or the event page's, framed apart (PRD.md, item 44).
type Crop = 'card' | 'page'

type Props = {
  snapshot: EventSnapshot
  disabled?: boolean
  // Shown only, as in the review before publishing.
  readOnly?: boolean
  onFocus: (crop: Crop, focus: Focus | null) => void
}

const clamp = (value: number) => Math.min(1, Math.max(0, Math.round(value * 1000) / 1000))
const KEY_STEP = 0.02

// The photo in one of the app's frames, cropped around the focus as the app
// crops it (an image's object-position); dragging it moves what shows.
function FocusFrame({ url, focus, ratio, label, disabled, onChange, children }: {
  url: string
  focus: Focus
  ratio: string
  label: string
  disabled?: boolean
  onChange: (focus: Focus) => void
  children?: ReactNode
}) {
  const frame = useRef<HTMLDivElement>(null)
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null)
  const drag = useRef<{ x: number; y: number; focus: Focus; overflowX: number; overflowY: number } | null>(null)

  // How far the photo reaches past the frame on each side, once covering it.
  const overflow = () => {
    const rect = frame.current?.getBoundingClientRect()
    if (!rect || !natural) return { overflowX: 0, overflowY: 0 }
    const scale = Math.max(rect.width / natural.width, rect.height / natural.height)
    return { overflowX: natural.width * scale - rect.width, overflowY: natural.height * scale - rect.height }
  }

  const start = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { x: event.clientX, y: event.clientY, focus, ...overflow() }
  }
  const move = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    if (!current) return
    onChange({
      x: current.overflowX > 0 ? clamp(current.focus.x - (event.clientX - current.x) / current.overflowX) : current.focus.x,
      y: current.overflowY > 0 ? clamp(current.focus.y - (event.clientY - current.y) / current.overflowY) : current.focus.y,
    })
  }
  const end = () => { drag.current = null }
  const key = (event: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, Focus> = {
      ArrowLeft: { x: -KEY_STEP, y: 0 }, ArrowRight: { x: KEY_STEP, y: 0 },
      ArrowUp: { x: 0, y: -KEY_STEP }, ArrowDown: { x: 0, y: KEY_STEP },
    }
    const step = moves[event.key]
    if (!step || disabled) return
    event.preventDefault()
    onChange({ x: clamp(focus.x + step.x), y: clamp(focus.y + step.y) })
  }

  return (
    <div
      ref={frame}
      className={`focus-frame ${disabled ? '' : 'focus-frame--editable'}`}
      style={{ aspectRatio: ratio }}
      tabIndex={disabled ? -1 : 0}
      role="group"
      aria-label={`${label}: arraste a foto ou use as setas para escolher o que aparece`}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={key}
    >
      <img
        src={url}
        alt=""
        draggable={false}
        style={{ objectPosition: `${focus.x * 100}% ${focus.y * 100}%` }}
        onLoad={(event) => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
      />
      {children}
    </div>
  )
}

// The event as the app will show it, beside the form and updated as it is
// filled in: its card and the top of its page. The photo is framed here,
// each frame on its own: dragging one leaves the other (docs/cms-hub.md).
export function EventPreview({ snapshot, disabled: busy, readOnly, onFocus }: Props) {
  const { event } = snapshot
  const disabled = busy || readOnly
  const editable = Boolean(event.image_url) && !readOnly
  const focusOf = (crop: Crop) => {
    const [x, y] = crop === 'card' ? [event.image_focus_x, event.image_focus_y] : [event.image_page_focus_x, event.image_page_focus_y]
    return { focus: { x: x ?? 0.5, y: y ?? 0.5 }, custom: x !== null && x !== undefined }
  }
  const { when, place, price } = eventPreview(snapshot)
  const title = event.title.trim() || 'Título do evento'
  const photo = (crop: Crop, ratio: string, label: string, overlay?: ReactNode) => event.image_url
    ? <FocusFrame url={event.image_url} focus={focusOf(crop).focus} ratio={ratio} label={label} disabled={disabled} onChange={(focus) => onFocus(crop, focus)}>{overlay}</FocusFrame>
    : <div className="focus-frame focus-frame--empty" style={{ aspectRatio: ratio }}><ImageOff size={22} /><span>Sem foto</span>{overlay}</div>
  const heading = (crop: Crop, label: string) => (
    <div className="app-preview__label-row">
      <span className="app-preview__label">{label}</span>
      {editable && (
        <button type="button" className="text-button" disabled={disabled || !focusOf(crop).custom} aria-label={`Centralizar: ${label}`} onClick={() => onFocus(crop, null)}>
          <Crosshair size={14} /> Centralizar
        </button>
      )}
    </div>
  )

  return (
    <aside className="app-preview" aria-label="Prévia no app">
      <div className="app-preview__heading">
        <div>
          <strong>Prévia no app</strong>
          <span>{editable ? 'Arraste cada foto para escolher o que aparece nela.' : 'Como o evento aparece para o público.'}</span>
        </div>
      </div>

      <div className="app-preview__item">
      {heading('card', 'Card')}
      <div className="preview-card">
        {photo('card', '350 / 190', 'Card')}
        <strong>{title}</strong>
        <span>{when}</span>
        <span>{place}</span>
      </div>
      </div>

      <div className="app-preview__item">
      {heading('page', 'Página do evento')}
      <div className="preview-page">
        {photo('page', '390 / 315', 'Página do evento', (
          <div className="preview-page__header" aria-hidden="true">
            <span><ChevronLeft size={15} /></span>
            <span className="preview-page__spacer" />
            <span><Share size={13} /></span>
            <span><Heart size={13} /></span>
          </div>
        ))}
        <div className="preview-page__content">
          <strong>{title}</strong>
          <span>{when}</span>
          <span>{place}</span>
          <span className="preview-page__price">{price}</span>
        </div>
      </div>
      </div>

    </aside>
  )
}
